import { useState, useRef, useEffect } from 'react';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;

export default function ChatBox() {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim() || !email.trim() || !message.trim()) {
      setErrorMessage('Please fill in all fields');
      setSubmitStatus('error');
      return;
    }

    if (message.length > 500) {
      setErrorMessage('Message must be 500 characters or less');
      setSubmitStatus('error');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address');
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/chat-inquiry-handler`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, message }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        setName('');
        setEmail('');
        setMessage('');
        setTimeout(() => {
          setSubmitStatus('idle');
          setIsOpen(false);
        }, 3000);
      } else {
        setErrorMessage(data.error || 'Failed to send message');
        setSubmitStatus('error');
      }
    } catch (error) {
      setErrorMessage('Network error. Please try again.');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const characterCount = message.length;
  const isOverLimit = characterCount > 500;

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 md:bottom-6 md:right-6 w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-[#000080] to-[#000060] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center z-50 hover:scale-110 cursor-pointer"
        aria-label="Open chat"
      >
        {isOpen ? (
          <i className="ri-close-line text-xl md:text-2xl"></i>
        ) : (
          <i className="ri-message-3-line text-xl md:text-2xl"></i>
        )}
      </button>

      {/* Chat Panel */}
      <div
        className={`fixed bottom-20 right-4 md:bottom-24 md:right-6 w-[calc(100vw-2rem)] max-w-96 bg-white rounded-2xl shadow-2xl transition-all duration-300 z-50 ${
          isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#000080] to-[#000060] text-white p-4 md:p-5 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-customer-service-2-line text-lg md:text-xl"></i>
            </div>
            <div>
              <h3 className="font-semibold text-base md:text-lg whitespace-nowrap">How can we help?</h3>
              <p className="text-xs md:text-sm text-white/80 whitespace-nowrap">We typically reply within 24 hours</p>
            </div>
          </div>
        </div>

        {/* Chat Body */}
        <div className="p-4 md:p-6">
          {submitStatus === 'success' ? (
            <div className="text-center py-6 md:py-8">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-2xl md:text-3xl text-green-600"></i>
              </div>
              <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-2 whitespace-nowrap">Message Sent!</h4>
              <p className="text-xs md:text-sm text-gray-600 whitespace-nowrap">We'll get back to you soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
              {/* Name Input */}
              <div>
                <label htmlFor="chat-name" className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  id="chat-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#000080] focus:border-transparent outline-none transition-all text-xs md:text-sm cursor-text"
                  placeholder="Your name"
                  disabled={isSubmitting}
                />
              </div>

              {/* Email Input */}
              <div>
                <label htmlFor="chat-email" className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="chat-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#000080] focus:border-transparent outline-none transition-all text-xs md:text-sm cursor-text"
                  placeholder="your@email.com"
                  disabled={isSubmitting}
                />
              </div>

              {/* Message Input */}
              <div>
                <label htmlFor="chat-message" className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  id="chat-message"
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  maxLength={500}
                  className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#000080] focus:border-transparent outline-none transition-all resize-none text-xs md:text-sm cursor-text"
                  placeholder="How can we help you?"
                  disabled={isSubmitting}
                />
                <div className={`text-xs mt-1 text-right ${isOverLimit ? 'text-red-600' : 'text-gray-500'}`}>
                  {characterCount}/500 characters
                </div>
              </div>

              {/* Error Message */}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 md:px-4 py-2 md:py-3 rounded-lg text-xs md:text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  {errorMessage}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || isOverLimit}
                className="w-full bg-gradient-to-r from-[#FF6B35] to-[#FF8555] text-white py-2.5 md:py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer text-xs md:text-sm"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill"></i>
                    Send Message
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 md:px-6 pb-4 md:pb-5 text-center">
          <p className="text-xs text-gray-500 whitespace-nowrap">
            Powered by DataVerge Solutions
          </p>
        </div>
      </div>
    </>
  );
}
